import { cn, hourFormatInAmsterdam } from "@/shared/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";

const TIME_COL_WIDTH = "48px";
const MARKER_COL_WIDTH = "40px";
const CONTENT_COL_WIDTH = "1fr";

/**
 * Timeline layout component.
 *
 * ## Usage
 * The Timeline can be composed in two ways:
 *
 * **1. With TimelineEvent and TimelineDescription:**
 * ```tsx
 * <Timeline>
 *   <TimelineLine />
 *   <TimelineEntries>
 *     {entries.map(entry => (
 *       <TimelineItem key={entry.id}>
 *         <TimelineTime dateTime={entry.time}>{entry.time}</TimelineTime>
 *         <TimelineMarker />
 *         <TimelineEvent>{entry.title}</TimelineEvent>
 *         <TimelineDescription>{entry.description}</TimelineDescription>
 *       </TimelineItem>
 *     ))}
 *   </TimelineEntries>
 * </Timeline>
 * ```
 *
 * **2. With TimelineCard (instead of TimelineEvent/TimelineDescription):**
 * ```tsx
 * <Timeline>
 *   <TimelineLine />
 *   <TimelineEntries>
 *     {entries.map(entry => (
 *       <TimelineItem key={entry.id}>
 *         <TimelineTime dateTime={entry.time}>{entry.time}</TimelineTime>
 *         <TimelineMarker filled={!entry.read} />
 *         <TimelineCard>
 *           {entry.title}
 *         </TimelineCard>
 *       </TimelineItem>
 *     ))}
 *   </TimelineEntries>
 * </Timeline>
 * ```
 *
 * ## Empty State
 * For empty timelines, use the `TimelineEmptyMessage` component to display a message aligned with the timeline grid:
 * ```tsx
 * {!entries.length ? (
 *   <TimelineEmptyMessage>No entries</TimelineEmptyMessage>
 * ) : (
 *   <TimelineEntries>...</TimelineEntries>
 * )}
 * ```
 *
 * ## Customization
 * You can override the column widths by setting the following CSS variables via the `style` prop or a parent CSS class:
 * - `--time-col-width` (default: 40px)
 * - `--marker-col-width` (default: 40px)
 * - `--content-col-width` (default: 1fr)
 *
 * Example:
 * ```tsx
 * <Timeline style={{ "--time-col-width": "60px" }}>
 *   ...
 * </Timeline>
 * ```
 */
export function Timeline({ className, style, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            className={cn("relative", className)}
            style={
                {
                    "--time-col-width": TIME_COL_WIDTH,
                    "--marker-col-width": MARKER_COL_WIDTH,
                    "--content-col-width": CONTENT_COL_WIDTH,
                    ...style,
                } as React.CSSProperties
            }
            {...props}
        />
    );
}

/**
 * Container for timeline entries (renders an ordered list).
 */
export function TimelineEntries({ className, ...props }: React.ComponentProps<"ol">) {
    return <ol className={cn("relative", className)} {...props} />;
}

interface AnimatedTimelineItemProps {
    children?: React.ReactNode;
    className?: string;
}

/**
 * AnimatedTimelineItem
 *
 * Renders a list item (`<li>`) with enter and exit animations using Motion.
 *
 * - The enter animation expands the item's height and fades it in.
 * - The exit animation collapses the item's height and fades it out.
 *
 * **Note:**
 * For the exit animation to work, this component (and its siblings) should be wrapped in
 * Motion's `<AnimatePresence>` component.
 *
 * NOTE: this component should only be rendered when reduceMotion is false!
 *
 * @param {object} props - Component props
 * @param {React.ReactNode} [props.children] - Content to render inside the list item
 * @param {string} [props.className] - Additional CSS classes
 */

export function AnimatedTimelineItem({ children, className, ...props }: Readonly<AnimatedTimelineItemProps>) {
    return (
        <motion.li
            className={cn("relative overflow-hidden", className)}
            initial={{ height: 0, opacity: 0 }}
            animate={{
                height: "auto",
                opacity: 1,
                transition: { type: "spring", bounce: 0, opacity: { delay: 0.1 } },
            }}
            exit={{ height: 0, opacity: 0, transition: { duration: 0.6, type: "spring", bounce: 0, opacity: { duration: 0.12 } } }}
            {...props}
        >
            {children}
        </motion.li>
    );
}

/**
 * A single timeline entry (renders a list item).
 */
export function TimelineItem({ className, animated = false, ...props }: React.ComponentProps<"li"> & { animated?: boolean }) {
    const prefersReducedMotion = useReducedMotion();
    const classNames = cn(
        "grid grid-cols-[var(--time-col-width)_var(--marker-col-width)_var(--content-col-width)_auto] grid-rows-[auto_1fr] items-center",
        className
    );
    const Component = animated && !prefersReducedMotion ? AnimatedTimelineItem : "li";

    return <Component className={classNames} {...props} />;
}

/**
 * Time label for a timeline entry.
 */
export function TimelineTime({ className, ...props }: React.ComponentProps<"time">) {
    return <time className={cn("text-content-emphasis text-right", className)} {...props} />;
}

/**
 * Time label for a timeline entry.
 */
export function TimelineDate({ className, ...props }: React.ComponentProps<"time">) {
    return <time className={cn("text-content-emphasis col-start-1 self-start text-right text-xs font-normal", className)} {...props} />;
}

const timelineDotVariants = cva(
    "border-accent-blue-default relative z-10 h-3 w-3 rounded-full border-2 justify-self-center row-start-1 col-start-2",
    {
        variants: {
            filled: {
                true: "bg-accent-blue-default",
                false: "bg-elevated-2",
            },
        },
        defaultVariants: {
            filled: true,
        },
    }
);

interface TimelineMarkerProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof timelineDotVariants> {}

/**
 * Renders a timeline marker (dot), filled or outlined.
 */
export function TimelineMarker({ className, filled, ...props }: Readonly<TimelineMarkerProps>) {
    return <div className={cn(timelineDotVariants({ filled }), className)} {...props} />;
}

const timelineLineVariants = cva(
    "h-full w-0.5 justify-self-center absolute top-0 bottom-0 left-[calc(var(--time-col-width)_+_(var(--marker-col-width)_/_2))] -translate-x-1/2",
    {
        variants: {
            dashed: {
                true: "border-l-2 border-dashed border-accent-blue-default bg-transparent",
                false: "border-l-2 border-accent-blue-default",
            },
        },
        defaultVariants: {
            dashed: false,
        },
    }
);

interface TimelineLineProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof timelineLineVariants> {
    /**
     * If true, renders a timer indicator at the top of the timeline line.
     */
    showCurrentTime?: boolean;
}

function CurrentTime() {
    const [currentTime, setCurrentTime] = useState(hourFormatInAmsterdam(new Date()));
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(hourFormatInAmsterdam(new Date()));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="bg-accent-blue-default text-content-default-inverse rounded-b-l absolute top-0 flex -translate-x-[42.5px] items-center gap-2 px-2 pt-0.5 pb-0.75">
            <span className="relative flex size-2">
                <span className="bg-content-error-inverse animate-ping-infrequent absolute inline-flex h-full w-full rounded-full opacity-75"></span>
                <span className="bg-content-error-inverse relative inline-flex size-2 rounded-full"></span>
            </span>
            <time>{currentTime}</time>
        </div>
    );
}

/**
 * Vertical line for the timeline, can be solid or dashed.
 */
export function TimelineLine({ className, dashed = false, showCurrentTime = false, ...props }: Readonly<TimelineLineProps>) {
    return (
        <div className={cn(timelineLineVariants({ dashed }), className)} {...props}>
            {showCurrentTime && <CurrentTime />}
        </div>
    );
}

/**
 * Card container for timeline content (alternative to TimelineEvent/TimelineDescription).
 */
export function TimelineCard({ className, ...props }: React.ComponentProps<"div">) {
    return <div className={cn("border-default bg-elevated-2 rounded-m col-start-3 border-b px-3 py-2", className)} {...props} />;
}

/**
 * Title for a timeline event.
 */
export function TimelineEvent({ className, ...props }: React.ComponentProps<"div">) {
    return <div className={cn("col-start-3 row-start-1", className)} {...props} />;
}

/**
 * Description for a timeline event.
 */
export function TimelineDescription({ className, ...props }: React.ComponentProps<"div">) {
    return <div className={cn("col-start-3 row-start-2", className)} {...props} />;
}

type TimelineBorderProps = React.ComponentProps<"div"> & {
    showBorder: boolean;
};
/**
 * Description for a timeline event.
 */
export function TimelineBorder({ showBorder, className, ...props }: TimelineBorderProps) {
    return (
        <div
            className={cn("col-span-2 col-start-3 row-start-3 mt-2 self-end", showBorder && "border-b-default border-b", className)}
            {...props}
        />
    );
}

/**
 * Description for a timeline event.
 */
export function TimelineEndIcon({ className, ...props }: React.ComponentProps<"div">) {
    return <div className={cn("col-start-4 row-span-2 row-start-1 place-self-center", className)} {...props} />;
}

/**
 * Empty state message for timelines, aligned with timeline grid.
 * Pass the message as children.
 */
export function TimelineEmptyMessage({ children, className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            className={cn(
                "text-content-subtle grid grid-cols-[var(--time-col-width)_var(--marker-col-width)_var(--content-col-width)] grid-rows-[auto_1fr] items-center",
                className
            )}
            {...props}
        >
            <p className="col-start-3">{children}</p>
        </div>
    );
}
